import React, { useState, createContext, useContext } from 'react';
import api from '../service/Service';
import SaveContext from './SaveContext';
import FlowContext from './FlowContext';

/**
 * Definição do contexto que deve ser usado no componente
 * que exibe as bases de conhecimento.
 */
export const Context = createContext({

    // Guarda a lista de bases de conhecimento que foram obtidas por último.
    knowledgeBases: [],

    // Requisita todas as bases de conhecimento.
    fetch: () => {},

    // Requisita a lista de tipos de fontes de dados.
    fetchDataSourceTypes: () => {},

    // Requisita ao serviço a criação de uma base de conhecimento.
    createKnowledgeBase: () => {},

    // Requisita ao serviço a atualização de uma base de conhecimento.
    updateKnowledgeBase: (knowledgeBase) => {},

    // Requisita ao serviço a remoção de uma base de conhecimento.
    deleteKnowledgeBase: () => {},

    // Requisita ao serviço a criação de uma fonte de dados.
    createDataSource: () => {},

    // Requisita ao serviço a remoção de uma fonte de dados.
    deleteDataSource: () => {}

});


/**
 * Define o Provider do contexto para as bases de conhecimento.
 * Contém as implementações dos métodos definidos no Contexto acima.
 * 
 * @param {*} children
 */
export const ContextProviderKnowledgeBases = ({ children }) => {
    const flowContext = useContext(FlowContext);
    const [knowledgeBases, setKnowledgeBases] = useState([]);
    
    /**
     * Realiza uma requisição à API para obter as bases de conhecimento.
     * 
     * As bases de conhecimento obtidas são armazenadas no contexto e, caso ocorra
     * algum erro na chamada à API, a lista será zerada.
     * 
     */
    const fetch = async () => {
        let result = [];
        const chatbotId = flowContext.activeBotId;
        
        setKnowledgeBases([]);

        const response = await api.get(`chatbots/${chatbotId}/knowledgebases/`);
        
        if (response.status === 200 && response.data.length > 0) {
            setKnowledgeBases(response.data);
            result = response.data;
        }

        return result;
    };

    /**
     * Realiza a requisição à API para obter a lista com todos os tipos
     * de fontes de dados.
     * 
     */
    const fetchDataSourceTypes = async () => {
        const response = await api.get(`datasource_types`);
        return response.data;
    };

    /**
     * Realiza a requisição à API para criar uma base de conhecimento.
     * 
     */
    const createKnowledgeBase = async (knowledgeBase) => {
        const chatbotId = flowContext.activeBotId;
        const response = await api.post(`chatbots/${chatbotId}/knowledgebases`, knowledgeBase);
        
        if (response.status !== 201) {
            throw Error("ERROR");
        }

        return response.data;
    };

    /**
     * Realiza a requisição à API para atualizar uma base de conhecimento.
     * 
     * @param knowledgeBase Base de conhecimento que será atualizada.
     */
    const updateKnowledgeBase = async (knowledgeBase) => {
        const chatbotId = flowContext.activeBotId;
        const response = await api.put(`chatbots/${chatbotId}/knowledgebases/${knowledgeBase.id}`, knowledgeBase);
        
        if (response.status != 200) {
            throw Error("ERROR");
        }         
    };

    /**
     * Realiza a requisição à API para remover uma base de conhecimento.
     * 
     */
    const deleteKnowledgeBase = async (knowledgeBase) => {
        const chatbotId = flowContext.activeBotId;
        await api.delete(`chatbots/${chatbotId}/knowledgebases/${knowledgeBase.id}`);
    };

    /**
     * Realiza a requisição à API para criar uma fonte de dados.
     * 
     */
    const createDataSource = async (knowledgeBaseId, request) => {
        const chatbotId = flowContext.activeBotId;
        await api.post(`chatbots/${chatbotId}/knowledgebases/${knowledgeBaseId}/datasources/`, request);
    };

    /**
     * Realiza uma requisição à API para remover uma fonte de dados.
     */
    const deleteDataSource = async (knowledgeBaseId, dataSource) => {
        const chatbotId = flowContext.activeBotId;
        await api.delete(`chatbots/${chatbotId}/knowledgebases/${knowledgeBaseId}/datasources/${dataSource.id}`);
    };
    
    return (
        <Context.Provider value={{
            knowledgeBases,
            fetch,
            fetchDataSourceTypes,
            createKnowledgeBase,
            updateKnowledgeBase,
            deleteKnowledgeBase,
            createDataSource,
            deleteDataSource
        }}>
          {children}
        </Context.Provider>
    );
};

export default Context;